import { formatDate } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DocumentSource } from '@app/core/enumerations';
import { DocumentExt, ICoreMemberDTO, IDocumentResponseDTO } from '@app/core/model';
import { AppliedFilter } from '@app/core/model/appliedFilter.model';
import { AuthenticationService, DocumentsService, NotificationService } from '@app/core/services';
import { DocumentService } from '@app/core/services/document.service';
import { IcaHttpService } from '@app/core/services/ica.service-http.service';
import { OcrService } from '@app/core/services/ocr.service';
import { DocumentTypesList } from '@app/documents/models';
import { DocumentsContextService } from '@core/services/context/factory';
import { PlatformService } from '@core/services/context/platform.service';
import { ProductNames } from '@mynexus/mynexus-ui-lib';
import { ConfirmationService, FilterService, SelectItem } from 'primeng/api';
import { DatePicker } from 'primeng/datepicker';
import { Table } from 'primeng/table';
import { BehaviorSubject, combineLatest, finalize, of, Subscription } from 'rxjs';

const documentTypeDisplayNameMap = {
  'DenialNotification': 'Denial Notification',
  'AdditionalInformation': 'Additional Information',
  'AdverseBenefitDetermination': 'Adverse Benefit Determination',
  'DecisionTimeframeExtension': 'Decision Timeframe Extension',
  'AuthorizationDenial': 'Authorization Denial',
  'AuthorizationAdminDenial': 'Authorization Admin Denial',
  'AuthorizationExtension': 'Authorization Extension',
  'PartialDenial': 'Partial Denial',
};
@Component({
    selector: 'mnc-documents-documents-list',
    templateUrl: './documents-list.component.html',
    styleUrls: ['./documents-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DocumentsListComponent implements OnInit, OnChanges {
  @Input() documents: IDocumentResponseDTO[] = [];
  @Input() lineOfBusinessId: string;
  @Input() tenantId: string;
  @Input() authorizationByProductId: string;
  @Input() authorizationNumber: string;
  @Input() isExternal: boolean;
  @Input() filterable = false;
  @Input() selectable = false;
  @Input() isFaxDisplay = false;
  @Input() showChecked = false;
  @Input() showDelete = false;
  @Input() isDocumentsTab = false;
  @Input() fileSelected: IDocumentResponseDTO[] = [];
  @Input() cancelMove$ = new BehaviorSubject<boolean>(false);
  @Input() previewColumnFilter = false;
  @Input() providerNPI: string;
  @Input() tenantName: string;
  @Input() effectiveFromDate: string = null;
  @Input() memberDto: ICoreMemberDTO = null;
  @Input() showDownloadButton = true;
  @Input() showClearFiltersButton = true;
  @Input() parentContext = '';
  @Input() documentFilter: ((doc: IDocumentResponseDTO) => boolean) | null = null;
  @Output() deleteCompleted = new EventEmitter();
  @Output() previewDoc = new EventEmitter<Set<string>>();
  @Output() selectedDocuments = new EventEmitter<IDocumentResponseDTO[]>();
  @Output() selectedDocumentsChange = new EventEmitter<string[]>();
  @Output() editCompleted = new EventEmitter<IDocumentResponseDTO>();

  originalFaxDocs: IDocumentResponseDTO[] = [];
  faxDocs: IDocumentResponseDTO[] = [];
  currentRow: IDocumentResponseDTO | null = null;
  showDeleteModal = false;
  isLoading = false;
  fileSubscription!: Subscription;
  selectedRowIndex!: number;
  selectedIndexList = new Set<string>();
  dateColumnLabel = ''
  allSelected = false;
  selectedDocumentIds: string[] = [];
  encryptedLink: string;
  isHHTenant = false;
  // Filters
  appliedFilters: Record<string, AppliedFilter> = {
    documentFileName: {
      matchMode: '',
      value: '',
    },
    documentSource: {
      matchMode: '',
      value: '',
    },
    documentType: {
      matchMode: '',
      value: '',
    },
    documentDescription: {
      matchMode: '',
      value: '',
    },
    dateTimeFaxReceived: {
      matchMode: '',
      value: '',
    },
    createdBy: {
      matchMode: '',
      value: '',
    },
    checkedByUserName: {
      matchMode: '',
      value: '',
    }
  };

  documentCheckedOptions = [{label:"Checked", value: true},{label:"Not Checked", value: false}];
  documentSourceList: SelectItem[] = [];
  documentTypeList: SelectItem[] = [];
  createdByList: SelectItem[] = [];
  dateTimeFaxReceivedFilter: Date;
  isOCRProcessing = false;
  encryptedLinkAvailable = false;

  // default width for DTDR column
  documentReceivedTimeStyles = {};
  actionName: string;

  @ViewChild('dt', { static: true }) dataTable!: Table;
  @ViewChild('dateRangeFilter') dateTimeFaxReceived: DatePicker;

  // These are our allowed Sources, mainly used to check if document can be deleted
  allowedSources: string[] = [ DocumentSource.Upload,DocumentSource.InboundFaxes ];
  private readonly destroyRef = inject(DestroyRef);

  allowedShowingPHIChecked = false;

  constructor(
    private documentUtilityService: DocumentsService,
    private ref: ChangeDetectorRef,
    private notificationService: NotificationService,
    public contextService: PlatformService,
    private filterService: FilterService,
    private documentsContextService: DocumentsContextService,
    private ocrService: OcrService,
    private icaHttpService: IcaHttpService,
    private authenticationService: AuthenticationService,
    private confirmationService: ConfirmationService,
  ) {
  }

  private documentService = inject(DocumentService);

  ngOnInit(): void {
    this.dateColumnLabel = this.isFaxDisplay ? 'Date/Time Created On' : 'Document Received Date/Time';
    this.isHHTenant = this.contextService.widgetContext === ProductNames.HomeHealth;
    this.cancelMove$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isCancelled) => {
      if (isCancelled) {
        this.clearCheckedDocuments();
      }

      this.ref.markForCheck();
    });
    this.filterService.register('customDate', (value: string, filter: Date[]): boolean => {
      if (!value) {
        return false;
      }
      if (!filter) {
        return true;
      }

      const fromDateTime = filter[0];
      const toDateTime = filter[1];

      if (!fromDateTime || !toDateTime) {
        return true; // No date range selected, no filtering
      }

      // Strip off time for comparison
      const dateValue = new Date(new Date(value).toDateString());
      const fromDate = new Date(new Date(fromDateTime).toDateString());
      const toDate = new Date(new Date(toDateTime).toDateString());

      return dateValue >= fromDate && dateValue <= toDate;
    });

    // Custom filter to cast string into a truthy value, since CheckedByUserName is a string field, not a boolean
    this.filterService.register('documentChecked', (value:string, filter:boolean[]): boolean => {
      if (filter === undefined || filter === null) {
        return true;
      }

      // Empty strings eval to False, and other string (ie a username) will eval to True
      return filter.includes(Boolean(value));
    });

    // Tech-debt TODO: Remove this custom filter once task 197060 is implemented
    this.filterService.register('docType', (value: string, filter: string[]): boolean => {
      if (filter.length === 0) {
        return false;
      }

      // Converts documents with multiple types into an array instead of a string for comparison
      const docTypes = value.includes(', ') ? value.split(', ') : [value];
      // Checks if the documentTypes for a document contains the selected value in the filter
      return filter.some(type => docTypes.includes(type));
    });

    this.ocrService.ocrProcessing$
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (isProcessing) => {
          this.isOCRProcessing = isProcessing;
          this.ref.markForCheck();
        }
      }
    );

    this.allowedShowingPHIChecked = !!this.authenticationService.userGroups
      .find(group => ['HHOperationsAdmin', 'HHOperations'].includes(group))
      && !this.isExternal;

      // updated call with US#233944
    this.initializeFaxDocs();
  }

  ngOnChanges() {
    if(this.contextService?.widgetFeatures?.icaViewSummaryLinkEnabled){
      this.getICASummaryViewLink();
    }
  }

  clearFilters(): void {
    for (const filter in this.appliedFilters) {
      const matchMode = filter === 'dateTimeFaxReceived' ? 'customDate' : '';
      this.filterTable('', filter, matchMode);

      if (filter === 'dateTimeFaxReceived') {
        this.validateSelectedDates();
      }
    }
  }

  downloadDocs(): void {
    this.documentsContextService
      .downloadZip(this.tenantId, this.authorizationByProductId, this.selectedDocumentIds)
      .subscribe({
        next: (x) => {
          const url = window.URL.createObjectURL(x);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'Document Packet.zip';
          link.click();
          window.URL.revokeObjectURL(url);
        },
        error: () => this.notificationService.showError('The unable to download the document packet.')
    })
  }

  viewICASummary() {
    window.open(this.encryptedLink, '_blank');
  }

  getDocumentTypeDisplayName(documentType: string): string {
    return documentTypeDisplayNameMap[documentType] || documentType;
  }

  initializeFaxDocs() {
    const observables = this.getDocumentData();

    combineLatest(observables)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(({ docs, sources, types, createdBy }) => {
      const filteredDocs = this.documentFilter ? docs.filter(this.documentFilter) : docs;
      
      this.faxDocs = filteredDocs;
      if (this.documentFilter) {
        this.documentSourceList = this.generateFilterOptions(filteredDocs, 'documentSource');
        this.documentTypeList = this.generateFilterOptions(filteredDocs, 'documentType');
        this.createdByList = this.generateFilterOptions(filteredDocs, 'createdBy');
      } else {
        this.documentSourceList = sources;
        this.documentTypeList = types;
        this.createdByList = createdBy;
      }

      // Need to track the original set because of the complexity with the date filtering so we can refilter when the date
      // is cleared. Possible we may want to make a decision if we should just refetch the results from the DB again
      // or just track the set from the current working session.
      this.originalFaxDocs = filteredDocs;

      // Selected Documents are cleared if input is changed, as the selected document may no longer be available
      this.clearCheckedDocuments();
    });
  }

  filterTable(value: any, criteria: string, matchMode: string): void {
    this.dataTable.first = 0;
    switch (matchMode) {
      case 'customDate':
        if (value) {
          if (value[0] && value[1]) {
            this.clearCustomFilter(criteria);
            const currentResults = this.faxDocs;
            this.faxDocs = this.filterService.filter(currentResults, [criteria], value, 'customDate');
          }
        } else {
          this.clearCustomFilter(criteria);
        }
        break;
      case 'docType':
        // Tech-debt TODO: Remove this custom filter once task 197060 is implemented
        if (value.length !== 0) {
          this.clearCustomFilter(criteria);
          const currentResults = this.faxDocs;
          this.faxDocs = this.filterService.filter(currentResults, [criteria], value, 'docType');
        } else {
          this.clearCustomFilter(criteria);
        }
        break;
      default:
        this.dataTable.filter(value, criteria, matchMode);
        break;
    }

    this.appliedFilters[criteria].matchMode = matchMode;
    this.appliedFilters[criteria].value = value;
  }

  // Date and docType filters have to be handled uniquely because they are a custom filter. When the they are cleared,
  // we want to still persist existing filters. A bit of limitations with PrimeNG, so we have to reset the table
  // to the original set and apply any other existing filters.
  clearCustomFilter(criteria: string): void {
    this.faxDocs = this.originalFaxDocs;
    this.appliedFilters[criteria].value = null;
    for (const filter in this.appliedFilters) {
      if (filter !== criteria) {
        const appliedFilter = this.appliedFilters[filter];

        if (appliedFilter.value && appliedFilter.matchMode) {
          this.dataTable.filter(appliedFilter.value, filter, appliedFilter.matchMode)
        }
      }
    }
  }

  preview(event: Event, item: IDocumentResponseDTO, rowIndex: number) {
    event.preventDefault();
    if (this.fileSubscription) {
      this.fileSubscription.unsubscribe();
    }

    const authIdentifier = this.contextService.widgetContext === 'HH' ? this.authorizationNumber : this.authorizationByProductId;

    let file: DocumentExt;
    this.selectedRowIndex = rowIndex;
    this.selectedIndexList.add(item.docMetaDataId);
    this.previewDoc.emit(this.selectedIndexList);
    this.isLoading = true;
    this.fileSubscription = this.documentsContextService.getFileById(
      this.tenantId,
      authIdentifier,
      item.docMetaDataId,
    )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.ref.markForCheck();
        })
      )
      .subscribe(res => {
        file = res;
        if (file.fileContent) {
          this.previewFile(file);
        } else {
          this.notificationService.showError(
            `The document ${file.documentFileName} is no longer available .`
          );
        }
      });
  }

  clearCheckedDocuments(): void {
    this.fileSelected = [];
    this.selectedDocumentIds = [];
  }

  previewFile(file: DocumentExt): void {
    this.documentUtilityService.previewFileFaxDocs(file);
  }

  onServicesRowChange() {
    if (this.selectable) {
      this.handleSelectedDocuments(this.fileSelected);
    }
  }

  isAllowedSource(rowData: IDocumentResponseDTO) {
    return rowData && this.allowedSources.includes(rowData.documentSource);
  }

  showDeleteDialog(rowData: IDocumentResponseDTO) {
    this.currentRow = rowData;
    this.showDeleteModal = true;
  }

  cancelDelete() {
    this.showDeleteModal = false;
  }

  proceedDelete() {
    this.showDeleteModal = false;
    const currentRowData = this.currentRow as IDocumentResponseDTO;

    this.documentsContextService.deleteDocumentForMoveDocument(
        this.tenantId,
        currentRowData.authorizationId,
        currentRowData.authorizationNumber,
        currentRowData.docMetaDataId,
        currentRowData.documentFileName,
    )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.ref.markForCheck())
      )
      .subscribe({
        next: () => {
          this.deleteCompleted.emit();
        },
        error: (err) => {
          this.currentRow = null;
          this.notificationService.showError('An Error occurred while deleting documents');
          throw err;
        }
      });
  }

  getLabel(rowData: IDocumentResponseDTO): string {
    if (rowData.checkedByUserName) {
      const data = formatDate(new Date(rowData.checkedDate), 'MM/dd/yyyy', 'en-US');
      return `${rowData.checkedByUserName} ${data}`
    }

    return 'Check Document'
  }

  getClass(docId: string): boolean {
    return this.selectedIndexList.has(docId);
  }

  showConfirmationCheckPopup(rowData: IDocumentResponseDTO): void {
    this.actionName = rowData.checkedByUserName ? 'Uncheck' : 'Check';

    this.confirmationService.confirm({
      key: 'confirm-check-document',
      header: `${this.actionName} Document`,
      message: `Are you sure you want to ${this.actionName.toLowerCase()} this document?`,
      rejectLabel: 'Cancel',
      acceptLabel: `${this.actionName} Document`,
      acceptButtonStyleClass: this.actionName === 'Check' ? 'p-button-primary' : 'p-button-danger',
      rejectButtonStyleClass: 'p-button-info',
      acceptIcon: this.actionName === 'Check' && 'pi pi-check',
      accept: () => this.updateDocumentCheck(rowData),
    });
  }

  allSelectedUpdated(event): void {
    if (event.checked) {
      this.fileSelected = this.dataTable.filteredValue || this.dataTable.value;
      this.handleSelectedDocuments(this.fileSelected);
    } else {
      this.clearCheckedDocuments();
    }
  }

  updateDocumentReceivedTime(updatedDocument: IDocumentResponseDTO): void {
    this.editCompleted.emit(updatedDocument);
  }

  handleSelectedDocuments(selectedDocuments: IDocumentResponseDTO[]): void {
    const selectedFilteredDocuments = selectedDocuments.filter(x => {
      // Filter out EHR documents that don't exist yet
      if (x.documentSource === 'EHR' && !x.documentStorageLocation) {
        return false;
      }

      return !(x.documentSource === 'Letter' && !this.contextService.widgetFeatures.showLetterPreview);
    });

    this.selectedDocuments.emit(selectedFilteredDocuments);
    this.selectedDocumentIds = selectedFilteredDocuments.map(x => x.docMetaDataId);
    this.selectedDocumentsChange.emit(this.selectedDocumentIds);
  }

  // Arrow function to bind 'this' to the component
  isRowSelectable = ({ data: row }: { data: IDocumentResponseDTO }): boolean => {

    // Add conditions here to prevent row from being selected by select all checkbox
    const isUnselectable =
      (row.documentSource === 'EHR' && !row.documentStorageLocation) ||
      (row.documentSource === 'Letter' && !this.contextService.widgetFeatures.showLetterPreview);

    return !isUnselectable;
  };

  // TODO: ANGULAR21_TODO Validate
  validateSelectedDates(): void {
    const filter = this.dateTimeFaxReceived.el.nativeElement;
    const filterDates = this.appliedFilters['dateTimeFaxReceived']['value'];

    // Need to check if filterDates is an array or else the red border will be applied on the default/cleared filter state: ''
    const isValidFilterDates = filterDates && filterDates.constructor === Array;

    // filterDates[1] is the end date since we are only checking if that one is populated or not
    if (isValidFilterDates && filterDates[1] === null) {
      filter.classList.add('ng-invalid', 'ng-dirty');
      this.notificationService.showError('Select the start and end date to filter by a date range.', 'Unable to Filter by Exact Date');
    } else {
      filter.classList.remove('ng-invalid', 'ng-dirty');
    }
  }

  sendToOcr(item: IDocumentResponseDTO, rowIndex: number){
    this.isOCRProcessing = true;
    this.selectedRowIndex = rowIndex;

    const authIdentifier = this.isHHTenant ? this.authorizationNumber : this.authorizationByProductId;
    this.ocrService.processDocumentToOcr(this.documentsContextService,item, this.tenantId, authIdentifier, this.lineOfBusinessId, this.providerNPI, this.authorizationByProductId);
  }

  isValidDocumentAvailable(){
    if(this.documents){
      return this.documents.length > 0 && this.documents.some(file => file.uploadReceivedDate != null);
    }
    return false;
  }

  setWarningSign(rowData){
    if(this.contextService?.widgetFeatures?.isIcaEnable && this.isDocumentRecentlyUploaded(rowData)){
      return rowData.uploadReceivedDate == null && this.isDocumentsTab && !this.isExternal && (rowData.documentSource === 'Upload' || rowData.documentSource === 'Inbound Faxes');
    }
    return false;
  }

  getICASummaryViewLink(){
    this.icaHttpService.getICADocumentLink(this.authorizationByProductId, this.tenantId)
    .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
        if(res) {
          this.encryptedLink = res;
          this.encryptedLinkAvailable = this.isDocumentsTab && !this.isExternal;
        }
        else {
          this.encryptedLinkAvailable = false;
        }
        this.ref.detectChanges();
      }
    });
  }

  isDocumentRecentlyUploaded(rowData){
      const now = new Date();
      const createdOnUtcDateWithZ = rowData.createdOn + "Z";
      const createdOnLocalDate = new Date(createdOnUtcDateWithZ);
      const timeDifference = (now.getTime() - createdOnLocalDate.getTime())/60000;
      return timeDifference > 2;
  }
   
  private generateFilterOptions(documents: IDocumentResponseDTO[], field: keyof IDocumentResponseDTO): SelectItem[] {
    const uniqueValues = new Set<string>();
    
    documents.forEach(doc => {
      const value = doc[field];
      if (value) {
        uniqueValues.add(value as string);
      }
    });
    
    return Array.from(uniqueValues)
      .sort()
      .map(value => ({ label: value, value: value }));
  }

  private getDocumentData() {
    return {
      docs: this.documentService.documents$,
      sources: this.documentService.sourceFilters$,
      types: this.documentService.typeFilters$,
      createdBy: this.documentService.createdByFilters$
    };
  }


  private updateDocumentCheck(rowData: IDocumentResponseDTO): void {
    this.documentsContextService.updateDocumentCheck(this.tenantId, this.authorizationByProductId, rowData.docMetaDataId, Boolean(rowData.checkedByUserName))
      .subscribe({
        next: res => {
          rowData.checkedByUserName = res.checkedByUserName;
          rowData.checkedDate = res.checkedDate;

          // Need to reload documents for Documents widget
          window.dispatchEvent(new CustomEvent('reloadClinicalResponses'));
        },
        complete: () => this.ref.markForCheck(),
      });
  }
}

